'use client';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useRef, useState, useEffect } from 'react';
import { AngleDownSmall } from '@zooplus/icons';
import { getTestLabels } from '../../../utils/getTestLabels.js';
import { H4, H3, H2 } from '../../Typography/Heading.js';
import '../../Typography/Paragraph.js';
import '../../Typography/TitleText.js';
import style from '../Accordion.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('AccordionItem');
const ACCORDION_ITEM_CLASS = 'z-accordion__item';
const ACCORDION_ITEM_CLOSING_CLASS = 'z-accordion__item--is-closing';
const ACCORDION_ITEM_OPENING_CLASS = 'z-accordion__item--is-opening';
const AccordionHeading = (props) => {
    const { children, simpleVersion, titleElement } = props;
    const headingProps = {
        className: simpleVersion ? undefined : style['z-accordion__item__title'],
        'data-zta': elementTestLabel('Title'),
    };
    const contents = (jsxs(Fragment, { children: [children, simpleVersion ? null : (jsx(AngleDownSmall, { className: style['z-accordion__marker'], "aria-hidden": true }))] }));
    switch (titleElement) {
        case 'h3':
            return jsx(H3, { ...headingProps, children: contents });
        case 'h4':
            return jsx(H4, { ...headingProps, children: contents });
        case 'h5':
            return jsx("h5", { ...headingProps, children: contents });
        case 'h6':
            return jsx("h6", { ...headingProps, children: contents });
    }
    return jsx(H2, { ...headingProps, children: contents });
};
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=30-1964&node-type=section&t=EDysr1f69m8d1p4V-0)
 * [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Accordion/AccordionItem/AccordionItem.tsx)
 *
 * @param {boolean} defaultOpen - (optional) Set to `true` have the panel be expanded by default.
 * @param {('large' | 'small')} size - (optional) Set to `small` to apply reduced font-size of title (defaults to `large`).
 * @param {string} title - The contents of the `<summary>` element.
 * @param {('h2' | 'h3' | 'h4' | 'h5' | 'h6')} titleElement - (optional) An HTML element for the title text. Can be h2-h6. Defaults to h2.
 */
const AccordionItem = ({ children, defaultOpen = false, size = 'large', title, titleElement = 'h2', ...otherProps }) => {
    const ref = useRef(null);
    const contentRef = useRef(null);
    const summaryRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);
    const [isOpening, setIsOpening] = useState(false);
    // see https://stackoverflow.com/a/69958975/17252
    useEffect(() => {
        if (!ref.current || !contentRef.current || !summaryRef.current) {
            return;
        }
        const detailsElem = ref.current;
        const summaryElem = summaryRef.current;
        const contentElem = contentRef.current;
        const handleSummaryClick = (e) => {
            if (!detailsElem) {
                return;
            }
            if (!detailsElem.hasAttribute('open')) {
                setIsOpening(true);
                detailsElem.classList.add(ACCORDION_ITEM_OPENING_CLASS);
                return;
            }
            // prevent the browser from removing the 'open' attribute on the
            // details element (will be done later in animationend handler)
            e.preventDefault();
            setIsClosing(true);
            detailsElem.classList.add(ACCORDION_ITEM_CLOSING_CLASS);
        };
        summaryElem.addEventListener('click', handleSummaryClick);
        const handleAnimationEnd = (e) => {
            const targetDetailsELem = e.target.closest(`.${ACCORDION_ITEM_CLASS}`);
            const currentTargetDetails = e.currentTarget.closest(`.${ACCORDION_ITEM_CLASS}`);
            if (!detailsElem || targetDetailsELem !== currentTargetDetails) {
                return;
            }
            if (e.animationName === 'z-accordion-anim-open') {
                setIsOpening(false);
                detailsElem.classList.remove(ACCORDION_ITEM_OPENING_CLASS);
            }
            if (
            // double-hyphen name variant for backward compatibility with v5
            e.animationName === 'z-accordion-anim-close' ||
                e.animationName === 'z-accordion--anim-close') {
                setIsClosing(false);
                detailsElem.classList.remove(ACCORDION_ITEM_CLOSING_CLASS);
                detailsElem.removeAttribute('open');
            }
        };
        contentElem.addEventListener('animationend', handleAnimationEnd);
        return () => {
            summaryElem.removeEventListener('click', handleSummaryClick);
            contentElem.removeEventListener('animationend', handleAnimationEnd);
        };
    }, []);
    return (jsxs(Fragment, { children: [jsxs("details", { open: true, className: style[`${ACCORDION_ITEM_CLASS}--disabled`], children: [jsx("summary", { "data-zta": elementTestLabel('Summary'), children: jsx(AccordionHeading, { simpleVersion: true, titleElement: titleElement, children: title }) }), jsx("div", { "data-zta": elementTestLabel('Content'), children: jsx("div", { children: children }) })] }), jsxs("details", { "data-zta": componentTestLabel, ref: ref, open: defaultOpen, ...otherProps, className: classNames(style[ACCORDION_ITEM_CLASS], {
                    [style[`${ACCORDION_ITEM_CLASS}--small`]]: size === 'small',
                    [style[ACCORDION_ITEM_CLOSING_CLASS]]: isClosing,
                    [style[ACCORDION_ITEM_OPENING_CLASS]]: isOpening,
                }), children: [jsx("summary", { ref: summaryRef, className: style['z-accordion__summary'], "data-zta": elementTestLabel('Summary'), children: jsx(AccordionHeading, { titleElement: titleElement, children: title }) }), jsx("div", { ref: contentRef, className: style['z-accordion__content'], "data-zta": elementTestLabel('Content'), children: jsx("div", { className: style['z-accordion__content-wrap'], children: jsx("div", { className: style['z-accordion__content-inner-wrap'], children: children }) }) })] })] }));
};

export { AccordionItem };
//# sourceMappingURL=AccordionItem.js.map
